import { useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { clock, doingWords, STEP_WORDS } from '@/lib/studio/format';
import { STUDIO_STAGES, type SceneState, type StepKind, type StudioStage, type StudioState, type StudioStep } from '@/lib/studio/studioState';

const LABELS: Record<StudioStage, string> = { plan: 'Plan', voice: 'Voice', build: 'Build', review: 'Review', deliver: 'Deliver' };
const AHEAD: Record<StudioStage, string> = {
  plan: 'the scenes and what they say',
  voice: 'the narration, recorded first',
  build: 'each scene, built and checked',
  review: 'you watch, then send notes',
  deliver: 'a share link and an MP4',
};
const DONE = 'bg-[hsl(var(--b-text-3)/0.55)]';
const STAGE_OF_KIND: Record<StepKind, StudioStage> = {
  look: 'plan', plan: 'plan', storyboard: 'plan', narration: 'voice', assemble: 'review', publish: 'review', check: 'review', export: 'deliver',
};
const SHORT: Record<StudioStage, string> = { plan: 'Planning', voice: 'Recording', build: 'Building', review: 'Reviewing', deliver: 'Exporting' };
const SCENE_NOTE: Record<SceneState['status'], string> = { planned: 'Waiting', building: 'Building', built: 'Built', 'needs-attention': 'Needs a look' };
const NEEDS: Record<NonNullable<StudioState['waiting']>['kind'], string> = { plan: 'approve the plan', review: 'look at the video', other: 'reply in the chat' };

interface Props {
  stage: StudioStage;
  waiting: StudioState['waiting'];
  working: boolean;
  sceneCounts: { built: number; total: number };
  /** Minutes left on the build, when there is a pace to go on. */
  eta?: number | null;
  /** The detail under each stage: Ivy's steps, the scenes, the clock they run on, the whole build's time once built. */
  steps?: StudioStep[];
  scenes?: SceneState[];
  now?: number;
  builtIn?: number;
  /** On a phone: the chip is one short word, with the whole line in its title. */
  compact?: boolean;
  /** You have sent Ivy a note since the build; until then her work at review is finishing the video. */
  notesSinceBuild?: boolean;
}

/**
 * Direction B's progress (beta only): Plan → Deliver as a 2 px line along the bottom of the top bar, and a chip only
 * while Ivy works or needs you. The chip or the line opens the five steps.
 */
export function StudioProgressB({ stage, waiting, working, sceneCounts, eta = null, steps = [], scenes = [], now = Date.now(), builtIn, compact = false, notesSinceBuild = true }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const current = STUDIO_STAGES.indexOf(stage);
  const building = stage === 'build' && sceneCounts.total > 0;
  const fillPct = waiting ? 100 : building ? Math.round((sceneCounts.built / sceneCounts.total) * 100) : working ? 50 : 100;
  const scenesDone = `${sceneCounts.built} of ${sceneCounts.total} scenes done`;
  const finishing = stage === 'review' && !notesSinceBuild;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const onDown = (e: Event) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    window.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown);
    };
  }, [open]);

  const full = waiting
    ? { lead: 'Ivy needs you', rest: `· ${NEEDS[waiting.kind]}` }
    : working
      ? { lead: doingWords(stage, notesSinceBuild), rest: building && sceneCounts.built < sceneCounts.total ? `· ${sceneCounts.built} of ${sceneCounts.total} done${eta ? ` · about ${eta} min` : ''}` : null }
      : null;
  const chip = full && compact ? { lead: waiting ? 'You' : finishing ? 'Finishing' : SHORT[stage], rest: null, title: [full.lead, full.rest].filter(Boolean).join(' ') } : full;

  const noteFor = (s: StudioStage, i: number) => {
    if (i < current) return 'done';
    if (i > current) return AHEAD[s];
    if (waiting) return 'waiting for you';
    if (building) return scenesDone;
    return working ? 'working on it' : AHEAD[s];
  };

  return (
    <div ref={ref} className="flex min-w-0 items-center">
      {chip && (
        <button
          type="button"
          data-progress-chip
          title={chip && 'title' in chip ? chip.title : undefined}
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          className={cn(
            'mr-2 flex h-7 min-w-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3 text-[12.5px] font-medium transition-colors',
            waiting ? 'bg-warning/15 text-b-t1 hover:bg-warning/25' : 'bg-b-raised text-b-t2 hover:bg-b-elevated',
          )}
        >
          {waiting ? (
            <span aria-hidden="true" className="h-[7px] w-[7px] flex-none rounded-full bg-warning" />
          ) : (
            <span aria-hidden="true" className="h-[7px] w-[7px] flex-none rounded-full bg-primary motion-safe:animate-pulse" />
          )}
          {chip.lead}
          {chip.rest && <span className="min-w-0 truncate text-b-t3">{chip.rest}</span>}
        </button>
      )}

      <button
        type="button"
        data-hairline
        aria-label={`Progress: ${LABELS[stage]}, ${waiting ? 'waiting for you' : building ? scenesDone : working ? 'working' : AHEAD[stage]}`}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="group absolute inset-x-0 bottom-0 flex h-1.5 items-end"
      >
        <span className="grid h-0.5 w-full grid-cols-5 gap-[3px] transition-[height] group-hover:h-1">
          {STUDIO_STAGES.map((s, i) => {
            const state = i < current ? 'done' : i === current ? 'current' : 'todo';
            return (
              <span key={s} data-seg={s} data-state={state} className={cn('relative overflow-hidden', state === 'done' ? DONE : 'bg-b-line-strong')}>
                {state === 'current' && (
                  <span data-fill style={{ width: `${fillPct}%` }} className={cn('absolute inset-y-0 left-0 transition-[width] duration-500', waiting ? 'bg-warning' : working ? 'bg-primary' : DONE)} />
                )}
              </span>
            );
          })}
        </span>
      </button>

      {open && (
        <div role="dialog" aria-label="Progress" className="absolute right-3 top-full z-50 mt-1.5 max-h-[60vh] w-80 overflow-y-auto rounded-[var(--b-radius-card)] bg-b-elevated p-2 shadow-lg ring-1 ring-[hsl(var(--b-line-strong))]">
          {builtIn != null && <p className="px-2 pb-1.5 pt-0.5 text-[12px] text-b-t3">Built in {Math.max(1, Math.round(builtIn / 60_000))} min</p>}
          <ol className="flex flex-col gap-0.5 text-[13px]">
            {STUDIO_STAGES.map((s, i) => {
              const state = i < current ? 'done' : i === current ? 'current' : 'todo';
              return (
                <li key={s} data-state={state}>
                  <div className="flex min-h-[30px] items-center gap-2.5 px-2">
                  <span aria-hidden="true" className="grid h-[18px] w-[18px] flex-none place-items-center">
                    {state === 'done' && <span className="grid h-[18px] w-[18px] place-items-center rounded-full bg-success/15 text-success"><Check className="h-3 w-3" /></span>}
                    {state === 'current' && <span className={cn('h-2 w-2 rounded-full', waiting ? 'bg-warning' : working ? 'bg-primary motion-safe:animate-pulse' : 'bg-b-ink')} />}
                    {state === 'todo' && <span className="h-2.5 w-2.5 rounded-full shadow-[inset_0_0_0_1.5px_hsl(var(--b-line-strong))]" />}
                  </span>
                  <span className={cn('w-16 flex-none font-medium', state === 'todo' ? 'text-b-t3' : 'text-b-t1')}>{LABELS[s]}</span>
                  <span className="min-w-0 text-b-t3">{noteFor(s, i)}</span>
                  </div>
                  <StageLines stage={s} steps={steps} scenes={scenes} now={now} />
                </li>
              );
            })}
          </ol>
        </div>
      )}
      {working && !waiting && <span role="status" className="sr-only">Working on {LABELS[stage].toLowerCase()}</span>}
    </div>
  );
}

function Line({ title, note, time }: { title: string; note?: string; time?: string }) {
  return (
    <li className="grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-2 py-0.5">
      <span className="min-w-0 truncate text-b-t2">{title}{note && <span className="ml-1.5 text-b-t3">{note}</span>}</span>
      {time && <span className="font-mono text-[11.5px] tabular-nums text-b-t3">{time}</span>}
    </li>
  );
}

/** A stage's own lines: Ivy's steps in today's words, and under Build each scene, timed by Ivy's own work. */
function StageLines({ stage, steps, scenes, now }: { stage: StudioStage; steps: StudioStep[]; scenes: SceneState[]; now: number }) {
  const mine = steps.filter((x) => STAGE_OF_KIND[x.kind] === stage);
  const sceneLines = stage === 'build' ? scenes : [];
  if (!mine.length && !sceneLines.length) return null;
  return (
    <ol data-lines={stage} className="mb-1 ml-[38px] mr-2 text-[12.5px]">
      {mine.map((x) => {
        const from = x.workStartedAt ?? x.startedAt;
        const time = x.status === 'running' ? clock(now - from) : x.endedAt != null ? clock(x.endedAt - from) : undefined;
        return <Line key={x.kind} title={STEP_WORDS[x.kind][x.status]} time={time} />;
      })}
      {sceneLines.map((c) => {
        const note = c.status === 'building' && c.drafts > 0 ? `Building · draft ${c.drafts}` : SCENE_NOTE[c.status];
        const time = c.status === 'building' && c.startedAt != null ? clock(now - c.startedAt) : c.status === 'built' && c.startedAt != null && c.endedAt != null ? clock(c.endedAt - c.startedAt) : undefined;
        return <Line key={c.id} title={`${c.order} · ${c.title}`} note={note} time={time} />;
      })}
    </ol>
  );
}
